import React, { useState, useEffect } from "react";
import "../../styles/Settings.scss";
import {
  createStatus,
  deleteStatus,
  getAllStatuses,
  getStatus,
  updateStatus,
} from "../../services/api";
import Modal from "react-modal";
import { FaEdit, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import ExcelJS from "exceljs";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function Statuses() {
  const [statuses, setStatuses] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // For download the data
  // const handleExport = () => {
  //   const csv = [
  //     "Name,Description",
  //     ...statuses.map(
  //       (s) =>
  //         `${s.name},${s.description || ""}`
  //     ),
  //   ].join("\n");
  //   const blob = new Blob([csv], { type: "text/csv" });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "statuses.csv";
  //   a.click();
  //   URL.revokeObjectURL(url);
  // };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Statuses");

    // Add header row
    worksheet.columns = [
      { header: "Name", key: "name" },
      // { header: "Description", key: "description" },
    ];

    // Add data rows
    statuses.forEach((item) => {
      worksheet.addRow({
        name: item.name || "-",
        // description: item.description || "",
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "statuses.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  // For pagination - page changes
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [search, statuses.length]);

  // for statuses filter along with pagination
  const filteredStatuses = statuses.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );
  const pageCount = Math.ceil(filteredStatuses.length / ITEMS_PER_PAGE);
  const paginatedStatuses = filteredStatuses.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // for create and edit modal
  const openCreate = () => {
    setEditStatus(null);
    setForm({ name: "", description: "" });
    setError("");
    setModalOpen(true);
  };

  const openEdit = async (status) => {
    // Fetch fresh data from API instead of using cached data
    await fetchStatusDetails(status.id);
  };

  const closeModal = () => setModalOpen(false);

  // To get All statuses
  const fetchStatuses = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAllStatuses();
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setStatuses(res.result_info);
      } else {
        setError(res?.error_info || "Failed to fetch statuses");
        setStatuses([]);
      }
    } catch (err) {
      setError("Failed to fetch statuses. Please try again.");
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  };
  // Fetch statuses on component mount
  useEffect(() => {
    fetchStatuses();
  }, []);

  // getting single status info
  const fetchStatusDetails = async (id) => {
    setEditLoading(true);
    setError("");
    try {
      const res = await getStatus(id);
      if (res && res.status === "S" && res.result_info) {
        const status = res.result_info;
        setForm({
          name: status.name || "",
          description: status.description || "",
        });
        setEditStatus(status);
        setModalOpen(true);
      } else {
        setError(res?.error_info || "Failed to fetch status details");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.error_info || "Failed to fetch status details",
        });
      }
    } catch (err) {
      setError("Failed to fetch status details. Please try again.");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch status details. Please try again.",
      });
    } finally {
      setEditLoading(false);
    }
  };
  // Create or Edit Status API Integration
  const handleSave = async () => {
    // Basic validations
    if (!form.name) {
      Swal.fire({
        text: "Please provide name!",
        icon: "warning",
        showConfirmButton: false,
        heightAuto: false,
        timer: 1500,
      });
      return;
    }

    if (form.name.length > 255) {
      Swal.fire({
        text: "Name should be less than 255 characters",
        icon: "warning",
        showConfirmButton: false,
        heightAuto: false,
        timer: 1500,
      });
      return;
    }

    setError(""); // Clear previous error

    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || "",
    };

    try {
      let response;

      // Decide whether to create or update
      if (editStatus) {
        response = await updateStatus(payload, editStatus.id);
      } else {
        response = await createStatus(payload);
      }

      if (response?.status === "S") {
        Swal.fire({
          icon: "success",
          title: editStatus
            ? "Status updated successfully!"
            : "Status created successfully!",
          showConfirmButton: false,
          timer: 1500,
        });

        setModalOpen(false); // Close modal
        fetchStatuses(); // Refresh status list
      } else if (response?.result_code === 409){
        Swal.fire({
          icon: 'warning',
          title: 'Status with this name already exists!',
          showConfirmButton: false,
          timer: 1500,
        })
      }else {
        const errorMsg =
          response?.error_info ||
          `Failed to ${
            editStatus ? "update" : "create"
          } status. Please try again.`;
        setError(errorMsg);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      }
    } catch (err) {
      const errorMsg =
        err?.message ||
        `Failed to ${
          editStatus ? "update" : "create"
        } status. Please try again.`;
      setError(errorMsg);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMsg,
      });
    }
  };
  //   For deleting status
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const res = await deleteStatus(id);
        if (res && res.status === "S") {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Status has been deleted.",
            showConfirmButton: false,
            timer: 1500,
          });
          fetchStatuses(); // Refresh the list
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              res?.error_info || "Failed to delete status. Please try again.",
          });
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.message || "Failed to delete status. Please try again.",
        });
      }
    }
  };

  
  function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
  const pages = [];

  const half = Math.floor(maxPagesToShow / 2);
  let start = Math.max(currentPage - half, 1);
  let end = start + maxPagesToShow - 1;

  if (end > totalPages) {
    end = totalPages;
    start = Math.max(end - maxPagesToShow + 1, 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
}

  return (
    <div className="settings-page">
      <div className="settings-header-row">
        <div className="settings-title-group">
          <h1>Statuses</h1>
          {/* <p>Manage and track all statuses</p> */}
        </div>
        <button className="export-btn" onClick={handleExport}>
          Export
        </button>
      </div>
      <div className="settings-table-controls">
        <input
          className="settings-search"
          placeholder="Search Status"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
                          
        />
        <button className="create-btn" onClick={openCreate}>
          Create Status
        </button>
      </div>
      <div className="settings-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading statuses...
          </div>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th style={{ width: "80%" }}>Name</th>
                {/* <th>Description</th> */}
                {/* <th>Created Date</th> */}
                <th style={{ width: "20%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedStatuses.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    {statuses.length === 0
                      ? "No statuses found"
                      : "No statuses match your search"}
                  </td>
                </tr>
              ) : (
                paginatedStatuses.map((status) => (
                  <tr key={status.id}>
                    <td style={{ width: "80%" }}>{status.name}</td>
                    {/* <td>{status.description || "-"}</td> */}
                    {/* <td>{status.created_at || status.created || "-"}</td> */}
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-items-center">
                    <button
                        className="icon-btn edit-btn"
                        title="Edit"
                        onClick={() => openEdit(status)}
                        disabled={editLoading}
                      >
                        {editLoading && editStatus?.id === status.id ? (
                          <span style={{ fontSize: "12px" }}>...</span>
                        ) : (
                          <FaEdit size={18} />
                        )}
                      </button>
                      <button
                        className="icon-btn delete-btn"
                        title="Delete"
                        onClick={() => handleDelete(status.id)}
                      >
                        <FaTrash size={18} />
                      </button>
                      </div>
                      
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
     {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
      {/* Create and Edit Status Modal based on prop (editStatus) */}
      <Modal
        isOpen={modalOpen}
        onRequestClose={closeModal}
        className="settings-modal"
        overlayClassName="settings-modal-overlay"
      >
        <h2>{editStatus ? "Edit Status" : "Create Status"}</h2>
        <div className="modal-field">
          <label>Name *</label>
          <input
            value={form.name}
            onChange={(e) => {
              const value = e.target.value;
              // Don't allow if it doesn't start with a letter (but allow empty string)
                if (value !== "" && !/^[a-zA-Z]/.test(value)) {
                  return; // Reject if doesn't start with letter
                }
             const validPattern = /^[a-zA-Z0-9\- ]*$/;
              if (validPattern.test(value)) {
                setForm((f) => ({ ...f, name: value }));
              }
            }}
            
            placeholder="Enter status name"
            autoComplete="off"
          />
        </div>
        <div className="modal-field d-none">
          <label>Description</label>
          <textarea
            value={form.description}
            onChange={(e) => {
              const value = e.target.value;
              // Don't allow if it doesn't start with a letter (but allow empty string)
                if (value !== "" && !/^[a-zA-Z.]/.test(value)) {
                  return; // Reject if doesn't start with letter
                }
              const invalidPattern =
                /<script.*?>.*?<\/script>|[<>\\{}=:/""''--]/i;
              if (!invalidPattern.test(value)) {
                setForm((f) => ({ ...f, description: value }));
              }
            }}
            placeholder="Enter status description"
            autoComplete="off"
                        
          />
        </div>
        {error && (
          <div style={{ color: "#e53e3e", marginBottom: 10, fontWeight: 500 }}>
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button className="save-btn" onClick={handleSave}>
            {editStatus ? "Update" : "Create"}
          </button>
          <button className="cancel-btn" onClick={closeModal}>
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Statuses;
